/**
 * Labstep
 *
 * @module grid/SmartTableProtocolCondition/components/ToolPanel/InventoryFields
 * @desc Lists for Resource
 */

import type { EntitySearchMultiStaticLists } from 'labstep-web/components/Entity/Search/Multi/types';
import { MetadataActionCreateModalDropdown } from 'labstep-web/components/Metadata/Action/Create/Modal/Dropdown';
import { ProtocolValueFormCreateModalContainer } from 'labstep-web/components/ProtocolValue/Form/Create/Modal';
import { ICONS } from 'labstep-web/constants/icons';
import { useGridSmartTableProtocolConditionServiceFromContext } from 'labstep-web/contexts/grid/hook';
import { SectionHeader } from 'labstep-web/core/Header';
import { useToolPanelContext } from 'labstep-web/grid/Index/components/ToolPanel/context';
import { getMetadataColDef } from 'labstep-web/grid/SmartTableProtocolCondition/coldefs/metadata';
import { getProtocolValueAmountColumnDef } from 'labstep-web/grid/SmartTableProtocolCondition/coldefs/protocol-value/amount';
import { getProtocolValueResourceItemColumnDef } from 'labstep-web/grid/SmartTableProtocolCondition/coldefs/protocol-value/resourceItem';
import type { SmartTableColDef } from 'labstep-web/grid/SmartTableProtocolCondition/coldefs/types';
import { ToolPanelState } from 'labstep-web/grid/SmartTableProtocolCondition/components/ToolPanel';
import { EntityDataGridToolPanelListItem } from 'labstep-web/grid/SmartTableProtocolCondition/components/ToolPanel/ListItem';
import type { EntityView } from 'labstep-web/models/entity-view.model';
import type { Experiment } from 'labstep-web/models/experiment.model';
import { Metadata } from 'labstep-web/models/metadata';
import { MetadataType } from 'labstep-web/models/metadata/types';
import type { Protocol } from 'labstep-web/models/protocol.model';
import { ProtocolValue } from 'labstep-web/models/protocol-value.model';
import { groupBy } from 'lodash';
import { type Dispatch, type SetStateAction, useEffect } from 'react';

export const getProtocolConditionList = (
  entityView: EntityView,
  experimentOrProtocol: Experiment | Protocol,
  setProtocolValue: Dispatch<SetStateAction<ProtocolValue | null>>,
  selectMetadata: (
    metadata: Metadata,
    parentEntityClass: string,
  ) => void,
  colIds: string[],
): EntitySearchMultiStaticLists => {
  const toolPanelContext = useToolPanelContext();
  const gridSmartTableProtocolConditionService =
    useGridSmartTableProtocolConditionServiceFromContext();

  // Clear active variable when opening tool panel
  useEffect(() => {
    if (toolPanelContext?.activeVariable) {
      toolPanelContext.setActiveVariable(null);
    }
  }, []);

  const metadatasGroupedByLabel: Metadata[] = Object.values(
    groupBy(
      experimentOrProtocol.metadata_thread?.metadatas || [],
      'label',
    ),
  ).map((group) => group[0]);

  return (searchQuery: string | null | undefined) => (
    <>
      <SectionHeader
        label="Input Inventory Fields"
        info="Input inventory fields allow you to specify which resources are required and how much, and allow you to track which specific items were used and deduct the amount from your inventory automatically."
      />
      {(experimentOrProtocol?.protocol_values || [])
        .filter(
          (protocolValue) =>
            (!searchQuery ||
              searchQuery.length === 0 ||
              protocolValue.name
                ?.toLowerCase()
                .includes(searchQuery.toLowerCase())) &&
            protocolValue.is_input &&
            !protocolValue.variable_template,
        )
        .map((protocolValue) => (
          <EntityDataGridToolPanelListItem
            icon="flask"
            actionIcon="arrow right"
            text={protocolValue.label}
            onClick={() => {
              setProtocolValue(protocolValue);
              toolPanelContext?.setToolPanelState(
                ToolPanelState.protocol_value,
              );
            }}
          />
        ))}
      <ProtocolValueFormCreateModalContainer
        dataCy={
          'grid/SmartTableProtocolCondition/components/ToolPanel/InventoryFields:AddInputInventoryField'
        }
        experiment={experimentOrProtocol}
        entityName={ProtocolValue.entityName}
        isInput={true}
        actionComponentProps={{
          type: 'text',
          icon: 'plus',
          text: `Add Input Inventory Field`,
        }}
        disableAddAnother
        initialName={searchQuery || null}
        options={{
          onSuccess: ({ response }: any): void => {
            const { result, entities } = response;
            const protocolValue = new ProtocolValue({
              ...entities.protocol_value[`${result}`],
            });

            toolPanelContext?.setActiveVariable(protocolValue);
            toolPanelContext?.setToolPanelState(
              ToolPanelState.add_inventory_field,
            );
            const colDefs: SmartTableColDef[] = [
              getProtocolValueAmountColumnDef(
                experimentOrProtocol,
                protocolValue.reportingName,
              ),
              getProtocolValueResourceItemColumnDef(
                experimentOrProtocol,
                protocolValue.reportingName,
              ),
            ];
            gridSmartTableProtocolConditionService?.addColumnDefs(
              colDefs,
            );
          },
        }}
      />
      <SectionHeader
        label="Output Inventory Fields"
        info="Output inventory fields are for items that will be added to inventory as part of this experiment"
      />
      {(experimentOrProtocol?.protocol_values || [])
        .filter(
          (protocolValue) =>
            (!searchQuery ||
              searchQuery.length === 0 ||
              protocolValue.name
                ?.toLowerCase()
                .includes(searchQuery.toLowerCase())) &&
            protocolValue.is_output &&
            !protocolValue.variable_template,
        )
        .map((protocolValue) => (
          <EntityDataGridToolPanelListItem
            icon="flask"
            actionIcon="arrow right"
            text={protocolValue.label}
            onClick={() => {
              setProtocolValue(protocolValue);
              toolPanelContext?.setToolPanelState(
                ToolPanelState.protocol_value,
              );
            }}
          />
        ))}
      <ProtocolValueFormCreateModalContainer
        dataCy={
          'grid/SmartTableProtocolCondition/components/ToolPanel/InventoryFields:AddOutputInventoryField'
        }
        experiment={experimentOrProtocol}
        entityName={ProtocolValue.entityName}
        isInput={false}
        actionComponentProps={{
          type: 'text',
          icon: 'plus',
          text: `Add Output Inventory Field`,
        }}
        disableAddAnother
        initialName={searchQuery || null}
        options={{
          onSuccess: ({ response }: any): void => {
            const { result, entities } = response;
            const protocolValue = new ProtocolValue({
              ...entities.protocol_value[`${result}`],
            });

            toolPanelContext?.setActiveVariable(protocolValue);

            toolPanelContext?.setToolPanelState(
              ToolPanelState.add_inventory_field,
            );

            const colDefs: SmartTableColDef[] = [
              getProtocolValueAmountColumnDef(
                experimentOrProtocol,
                protocolValue.reportingName,
              ),
              getProtocolValueResourceItemColumnDef(
                experimentOrProtocol,
                protocolValue.reportingName,
              ),
            ];

            gridSmartTableProtocolConditionService?.addColumnDefs(
              colDefs,
            );
          },
        }}
      />
      <SectionHeader
        label="Input Data Fields"
        info="Use input data fields to capture variable input parameters"
      />
      {metadatasGroupedByLabel
        .filter(
          (metadata) =>
            (!searchQuery ||
              searchQuery.length === 0 ||
              metadata.label
                ?.toLowerCase()
                .includes(searchQuery.toLowerCase())) &&
            metadata.is_input &&
            !metadata.variable_template,
        )
        .map((metadata) => (
          <EntityDataGridToolPanelListItem
            icon={
              ICONS.metadata.type[
                metadata.type as keyof typeof ICONS.metadata.type
              ] || 'database'
            }
            actionIcon={
              metadata.type === MetadataType.molecule
                ? 'arrow right'
                : 'eye'
            }
            text={metadata.label || 'Untitled data field'}
            onClick={() => {
              selectMetadata(metadata, metadata.parent_entity_class);
            }}
            disabled={colIds.includes(metadata.colId)}
          />
        ))}
      <MetadataActionCreateModalDropdown
        entity={experimentOrProtocol}
        withVariableFields={{
          isInput: true,
        }}
        dataCy={
          'grid/SmartTableProtocolCondition/components/ToolPanel/InventoryFields:AddInputDataField'
        }
        text={'Add Input Data Field'}
        initialLabel={searchQuery || null}
        options={{
          onSuccess: ({ response }: any): void => {
            if (response.entities.metadata) {
              const metadatas = Object.values(
                response.entities.metadata,
              );

              metadatas.forEach((metadata: any) => {
                const coldef = getMetadataColDef(
                  experimentOrProtocol,
                  experimentOrProtocol.entityName,
                  metadata.type,
                  metadata.label || '',
                );
                gridSmartTableProtocolConditionService?.addColumnDef(
                  coldef,
                );
                toolPanelContext?.setActiveVariable(
                  new Metadata({
                    ...metadata,
                  }),
                );
              });
            }
          },
        }}
        onUnmount={() => {
          if (toolPanelContext?.activeVariable) {
            toolPanelContext?.setToolPanelState(
              ToolPanelState.add_data_field,
            );
          }
        }}
      />
      <SectionHeader
        label="Output Data Fields"
        info="Use output data fields to capture any raw data, along with other measurements / results such as yield or concentration"
      />
      {metadatasGroupedByLabel
        .filter(
          (metadata) =>
            (!searchQuery ||
              searchQuery.length === 0 ||
              metadata.label
                ?.toLowerCase()
                .includes(searchQuery.toLowerCase())) &&
            metadata.is_output &&
            !metadata.variable_template,
        )
        .map((metadata) => (
          <EntityDataGridToolPanelListItem
            icon={
              ICONS.metadata.type[
                metadata.type as keyof typeof ICONS.metadata.type
              ] || 'database'
            }
            actionIcon={
              metadata.type === MetadataType.molecule
                ? 'arrow right'
                : 'eye'
            }
            text={metadata.label || 'Untitled data field'}
            onClick={() => {
              selectMetadata(metadata, metadata.parent_entity_class);
            }}
            disabled={colIds.includes(metadata.colId)}
          />
        ))}
      <MetadataActionCreateModalDropdown
        entity={experimentOrProtocol}
        withVariableFields={{ isInput: false }}
        dataCy={
          'grid/SmartTableProtocolCondition/components/ToolPanel/InventoryFields:AddOutputDataField'
        }
        text={'Add Output Data Field'}
        initialLabel={searchQuery || null}
        options={{
          onSuccess: ({ response }: any): void => {
            if (response.entities.metadata) {
              const metadatas = Object.values(
                response.entities.metadata,
              );
              metadatas.forEach((metadata: any) => {
                const coldef = getMetadataColDef(
                  experimentOrProtocol,
                  experimentOrProtocol.entityName,
                  metadata.type,
                  metadata.label || '',
                );
                gridSmartTableProtocolConditionService?.addColumnDef(
                  coldef,
                );
                toolPanelContext?.setActiveVariable(
                  new Metadata({
                    ...metadata,
                  }),
                );
              });
            }
          },
        }}
        onUnmount={() => {
          if (toolPanelContext?.activeVariable) {
            toolPanelContext?.setToolPanelState(
              ToolPanelState.add_data_field,
            );
          }
        }}
      />
    </>
  );
};
